import React, { useState, useEffect } from 'react';
import Layout from './Layout';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { adminAPI } from '../../services/api';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import PaymentVoicePopup from '../../components/PaymentVoicePopup';
import { generatePaymentPDF } from '../../components/paymentPdfUtils';
import { getImageUrl } from '../../utils/imageUtils';

const AdminPayments = () => {
  const [dateRange, setDateRange] = React.useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [paymentStats, setPaymentStats] = useState({
    totalRevenue: 0,
    totalRevenueChange: 0,
    totalTransactions: 0,
    totalTransactionsChange: 0,
    failedPayments: 0,
    failedPaymentsChange: 0,
    pendingPayments: 0,
    pendingPaymentsChange: 0,
    // refunds: 0,
    // refundsChange: 0
  });
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState(null);

  // Payment table state
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(true);
  const [paymentsError, setPaymentsError] = useState(null);

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
    limit: 10
  });

  // Filter state
  const [filters, setFilters] = useState({
    search: '',
    startDate: null,
    endDate: null,
    status: 'All Status',
    paymentType: 'All Types'
  });

  // Popup state
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);



  // Fetch payment stats
  useEffect(() => {
    const fetchPaymentStats = async () => {
      try {
        console.log('🔄 Starting to fetch payment stats...');
        setStatsLoading(true);
        setStatsError(null);
        
        const response = await adminAPI.getPaymentSummary();
        console.log('✅ Payment stats response:', response);
        
        if (response) {
          setPaymentStats({
            totalRevenue: response.totalRevenue || response.total_revenue || 0,
            totalRevenueChange: response.totalRevenueChange || response.total_revenue_change || 0,
            totalTransactions: response.totalTransactions || response.total_transactions || 0,
            totalTransactionsChange: response.totalTransactionsChange || response.total_transactions_change || 0,
            failedPayments: response.failedPayments || response.failed_payments || 0,
            failedPaymentsChange: response.failedPaymentsChange || response.failed_payments_change || 0,
            pendingPayments: response.pendingPayments || response.pending_payments || 0,
            pendingPaymentsChange: response.pendingPaymentsChange || response.pending_payments_change || 0,
            // refunds: response.refunds || 0,
            // refundsChange: response.refundsChange || response.refunds_change || 0
          });
          console.log('✅ Payment stats updated:', {
            totalRevenue: response.totalRevenue || response.total_revenue || 0,
            totalRevenueChange: response.totalRevenueChange || response.total_revenue_change || 0,
            totalTransactions: response.totalTransactions || response.total_transactions || 0,
            totalTransactionsChange: response.totalTransactionsChange || response.total_transactions_change || 0,
            failedPayments: response.failedPayments || response.failed_payments || 0,
            failedPaymentsChange: response.failedPaymentsChange || response.failed_payments_change || 0,
            pendingPayments: response.pendingPayments || response.pending_payments || 0,
            pendingPaymentsChange: response.pendingPaymentsChange || response.pending_payments_change || 0,
            // refunds: response.refunds || 0,
            // refundsChange: response.refundsChange || response.refunds_change || 0
          });
        } else {
          throw new Error('Invalid response from API');
        }
      } catch (err) {
        console.error('❌ Error fetching payment stats:', err);
        setStatsError(err.message);
        
        // Keep default values on error
        setPaymentStats({
          totalRevenue: 0,
          totalRevenueChange: 0,
          totalTransactions: 0,
          totalTransactionsChange: 0,
          failedPayments: 0,
          failedPaymentsChange: 0,
          pendingPayments: 0,
          pendingPaymentsChange: 0,
          // refunds: 0,
          // refundsChange: 0
        });
      } finally {
        setStatsLoading(false);
        console.log('✅ Payment stats loading completed');
      }
    };

    fetchPaymentStats();
  }, []);

  // Fetch payment table data
  const fetchPayments = async (filterParams = {}, page = 1) => {
    try {
      console.log('🔄 Starting to fetch payment table data...');
      console.log('🔄 Filter params:', filterParams);
      console.log('🔄 Page:', page);
      
      setPaymentsLoading(true);
      setPaymentsError(null);
      
      // Add pagination to filter params
      const params = { ...filterParams, page, limit: pagination.limit };
      
      console.log('🔄 Final API params:', params);
      
      const response = await adminAPI.getRecentPaymentsDetailed(params);
      console.log('✅ Payment table response:', response);
      
      if (response && response.payments) {
        // Client-side date range filtering (fallback)
        let filteredPayments = response.payments;
        if (filters.startDate && filters.endDate) {
          const start = new Date(filters.startDate + 'T00:00:00');
          const end = new Date(filters.endDate + 'T23:59:59');
          filteredPayments = filteredPayments.filter(payment => {
            if (!payment.date) return false;
            const paymentDate = new Date(payment.date);
            return paymentDate >= start && paymentDate <= end;
          });
        }
        setPayments(filteredPayments);
        // Use API pagination data
        setPagination({
          currentPage: response.page || 1,
          totalPages: response.totalPages || 1,
          total: response.total || 0,
          limit: pagination.limit
        });
        console.log('✅ Payment table updated with', filteredPayments.length, 'payments');
        // Debug: Log all unique payment types
        const uniqueTypes = [...new Set(response.payments.map(p => p.type || p.paymentType))];
        console.log('🔍 Available payment types in API response:', uniqueTypes);
        // Debug: Log all available dates in the table
        const allDates = response.payments.map(p => ({
          id: p._id,
          date: p.date,
          formattedDate: p.date ? new Date(p.date).toISOString().split('T')[0] : 'No date'
        }));
        console.log('🔍 All available dates in table:', allDates);
      } else if (response && Array.isArray(response)) {
        setPayments(response);
        console.log('✅ Payment table updated with', response.length, 'payments');
      } else {
        setPayments([]);
        console.log('✅ No payments found, setting empty array');
      }
    } catch (err) {
      console.error('❌ Error fetching payment table data:', err);
      setPaymentsError(err.message);
      setPayments([]);
    } finally {
      setPaymentsLoading(false);
      console.log('✅ Payment table loading completed');
    }
  };

  // Fetch payment table data on component mount
  useEffect(() => {
    fetchPayments();
  }, []);

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    const newFilters = { ...filters, [filterType]: value };
    setFilters(newFilters);
    
    // Build filter parameters for API
    const filterParams = {};
    if (newFilters.search) filterParams.search = newFilters.search;
    if (newFilters.startDate) {
      filterParams.startDate = newFilters.startDate;
    }
    if (newFilters.endDate) {
      filterParams.endDate = newFilters.endDate;
    }
    if (newFilters.status && newFilters.status !== 'All Status') {
      // Convert status to lowercase to match API response
      filterParams.status = newFilters.status.toLowerCase();
    }
    if (newFilters.paymentType && newFilters.paymentType !== 'All Types') {
      filterParams.type = newFilters.paymentType;
      console.log('🔄 Payment type filter applied:', newFilters.paymentType);
    }
    
    console.log('🔄 Filter changed:', filterType, value);
    console.log('🔄 Sending filter params to API:', filterParams);
    
    // Reset to first page when filtering
    fetchPayments(filterParams, 1);
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      const filterParams = {};
      if (filters.search) filterParams.search = filters.search;
      if (filters.startDate) {
        filterParams.startDate = filters.startDate;
      }
      if (filters.endDate) {
        filterParams.endDate = filters.endDate;
      }
      if (filters.status && filters.status !== 'All Status') {
        filterParams.status = filters.status.toLowerCase();
      }
      if (filters.paymentType && filters.paymentType !== 'All Types') {
        filterParams.type = filters.paymentType;
      }
      
      fetchPayments(filterParams, newPage);
    }
  };

  function formatDateTime(datetimeStr) {
    const date = new Date(datetimeStr);
    const options = {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    };
    return date.toLocaleString('en-GB', options).replace(',', ' at');
  }

  const getPaymentTypeColor = (type) => {
    switch (type) {
      case 'Assessment': return 'bg-blue-100 text-blue-800';
      case 'Subscription': return 'bg-purple-100 text-purple-800';
      case 'Consultation': return 'bg-green-100 text-green-800';
      case 'Follow-up': return 'bg-pink-100 text-pink-800';
      case 'custom_price': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const downloadPaymentPDF = (payment) => {
    generatePaymentPDF(payment);
  };

  // Update the image URL in the mobile card layout
  const getPatientImage = (payment, index) => {
    const imagePath = payment.patient?.image || payment.image;
    if (imagePath) {
      return getImageUrl(imagePath);
    }
    return `https://i.pravatar.cc/150?img=${index + 1}`;
  };

  return (
    <div className="min-h-screen ">
      {/* Header */}
      <div className="mb-6 md:mb-8">
        <h1 className="text-xl md:text-2xl font-semibold text-gray-800">Payment Management</h1>
        <p className="text-gray-600 mt-2">Stay on top of your payments and billing information to ensure uninterrupted ADHD care.</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Total Revenue Card */}
        <div className="bg-green-50 border border-gray-200 rounded-lg shadow-lg p-5">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Total Revenue</h3>
            <span className={`text-xs px-2 py-1 rounded-full font-medium ${paymentStats.totalRevenueChange >= 0 ? 'text-green-600 bg-green-100' : 'text-red-600 bg-red-100'}`}>
              {paymentStats.totalRevenueChange >= 0 ? '+' : ''}{paymentStats.totalRevenueChange}%
            </span>
          </div>
          <p className="text-3xl font-bold text-gray-900">${paymentStats.totalRevenue.toFixed(2)}</p>
        </div>

        {/* Total Transactions Card */}
        <div className="bg-blue-50 border border-gray-200 rounded-lg shadow-lg p-5">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Completed Transactions</h3>
            <span className={`text-xs px-2 py-1 rounded-full font-medium ${paymentStats.totalTransactionsChange >= 0 ? 'text-blue-600 bg-blue-100' : 'text-red-600 bg-red-100'}`}>
              {paymentStats.totalTransactionsChange >= 0 ? '+' : ''}{paymentStats.totalTransactionsChange}%
            </span>
          </div>
          <p className="text-3xl font-bold text-gray-900">{paymentStats.totalTransactions}</p>
        </div>

        {/* Failed Payments Card */}
        <div className="bg-red-50 border border-gray-200 rounded-lg shadow-lg p-5">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Failed Payments</h3>
            <span className={`text-xs px-2 py-1 rounded-full font-medium ${paymentStats.failedPaymentsChange <= 0 ? 'text-green-600 bg-green-100' : 'text-red-600 bg-red-100'}`}>
              {paymentStats.failedPaymentsChange >= 0 ? '+' : ''}{paymentStats.failedPaymentsChange}%
            </span>
          </div>
          <p className="text-3xl font-bold text-gray-900">{paymentStats.failedPayments}</p>
        </div>

        {/* Pending Payments Card */}
        <div className="bg-yellow-50 border border-gray-200 rounded-lg shadow-lg p-5">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Pending Payments</h3>
            <span className={`text-xs px-2 py-1 rounded-full font-medium ${paymentStats.pendingPaymentsChange <= 0 ? 'text-green-600 bg-green-100' : 'text-yellow-600 bg-yellow-100'}`}>
              {paymentStats.pendingPaymentsChange >= 0 ? '+' : ''}{paymentStats.pendingPaymentsChange}%
            </span>
          </div>
          <p className="text-3xl font-bold text-gray-900">{paymentStats.pendingPayments}</p>
        </div>
      </div>

      {/* Filter Section - Responsive */}
      <div className="space-y-6">
        <div className="py-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">

                {/* Search */}
                <div>
                  <div className="relative width full">
                    <input
                      type="text"
                      placeholder="Search by name or email"
                      value={filters.search}
                      onChange={(e) => handleFilterChange('search', e.target.value)}
                      className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    />
                    <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Date Range */}
                <div>
                  <div className="relative">
                    <DatePicker
                      selectsRange
                      startDate={startDate}
                      endDate={endDate}
                      onChange={(update) => {
                        setDateRange(update);
                        if (update && update[0] && update[1]) {
                          const pad = n => n.toString().padStart(2, '0');
                          const startDateStr = `${update[0].getFullYear()}-${pad(update[0].getMonth() + 1)}-${pad(update[0].getDate())}`;
                          const endDateStr = `${update[1].getFullYear()}-${pad(update[1].getMonth() + 1)}-${pad(update[1].getDate())}`;
                          console.log('🔄 Date range selected:', { startDate: startDateStr, endDate: endDateStr });
                          
                          // Update filters and trigger API call
                          const newFilters = { ...filters, startDate: startDateStr, endDate: endDateStr };
                          setFilters(newFilters);
                          
                          // Build filter parameters for API
                          const filterParams = {};
                          if (newFilters.search) filterParams.search = newFilters.search;
                          // Use only startDate and endDate for payments API
                          filterParams.startDate = startDateStr;
                          filterParams.endDate = endDateStr;
                          if (newFilters.status && newFilters.status !== 'All Status') {
                            filterParams.status = newFilters.status.toLowerCase();
                          }
                          if (newFilters.paymentType && newFilters.paymentType !== 'All Types') {
                            filterParams.type = newFilters.paymentType;
                          }
                          
                          console.log('🔄 Date filter params being sent:', filterParams);
                          
                          // Reset to first page and fetch data
                          fetchPayments(filterParams, 1);
                        } else {
                          // Clear date filters
                          const newFilters = { ...filters, startDate: null, endDate: null };
                          setFilters(newFilters);
                          
                          // Build filter parameters for API
                          const filterParams = {};
                          if (newFilters.search) filterParams.search = newFilters.search;
                          if (newFilters.status && newFilters.status !== 'All Status') {
                            filterParams.status = newFilters.status.toLowerCase();
                          }
                          if (newFilters.paymentType && newFilters.paymentType !== 'All Types') {
                            filterParams.type = newFilters.paymentType;
                          }
                          
                          console.log('🔄 Cleared date filters, params:', filterParams);
                          
                          // Reset to first page and fetch data
                          fetchPayments(filterParams, 1);
                        }
                      }}
                      isClearable
                      placeholderText="Select date range"
                      className="w-full text-sm border px-16 border-gray-200 rounded-lg py-2  bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <div className="absolute inset-y-0  pl-4 flex items-center pointer-events-none">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Status Dropdown */}
                <div>
                  <div className="relative w-full">
                    <select 
                      value={filters.status}
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                      className="appearance-none w-full text-sm border border-gray-200 rounded-lg py-2 px-4 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option>All Status</option>
                      <option>Completed</option>
                      <option>Pending</option>
                      <option>Failed</option>
                      {/* <option>Refunded</option> */}
                    </select>

                    {/* Dropdown Icon */}
                    <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                      <svg
                        className="w-4 h-4 text-black"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Payment Type Dropdown */}
                <div>
                  <div className="relative w-full">
                    <select 
                      value={filters.paymentType}
                      onChange={(e) => handleFilterChange('paymentType', e.target.value)}
                      className="appearance-none w-full text-sm border border-gray-200 rounded-lg py-2 px-4 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option>All Types</option>
                      <option>monthly_subscription</option>
                      <option>weekly_subscription</option>
                      <option>reschedule_payment</option>
                    </select>

                    {/* Dropdown Icon */}
                    <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                      <svg
                        className="w-4 h-4 text-black"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Styled Table */}
         <div className="space-y-1">
  {/* Table Header - Desktop Only */}
  <div className="py-4 hidden md:block">
    <div className="grid grid-cols-12 gap-4 items-center text-xs font-medium text-gray-600 uppercase tracking-wider text-left bg-gray-100 p-4 rounded-lg">
      <div className="col-span-2 text-left">Patient</div>
      <div className="col-span-2 text-left">Payment Type</div>
      <div className="col-span-1 text-left">Amount</div>
      <div className="col-span-1 text-left">Method</div>
      <div className="col-span-2 text-left">Status</div>
      <div className="col-span-2 text-left">Date</div>
      <div className="col-span-1 text-left">Invoice</div>
      <div className="col-span-1 text-left">Actions</div>
    </div>
  </div>

  {/* Table Content */}
  <div className="space-y-2">
    {paymentsLoading ? (
      // Loading skeleton
      <>
        {/* Mobile Card Loading */}
        <div className="block md:hidden space-y-3">
          {Array.from({ length: 5 }).map((_, idx) => (
            <div key={idx} className="bg-white border rounded-xl p-4 shadow-sm border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <div className="w-10 h-10 bg-gray-200 rounded-full animate-pulse"></div>
                  <div>
                    <div className="h-4 bg-gray-200 rounded w-24 animate-pulse mb-1"></div>
                    <div className="h-3 bg-gray-200 rounded w-16 animate-pulse"></div>
                  </div>
                </div>
                <div className="h-6 bg-gray-200 rounded w-16 animate-pulse"></div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="h-6 bg-gray-200 rounded w-20 animate-pulse"></div>
                  <div className="h-4 bg-gray-200 rounded w-16 animate-pulse"></div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="h-4 bg-gray-200 rounded w-24 animate-pulse"></div>
                  <div className="text-right">
                    <div className="h-4 bg-gray-200 rounded w-20 animate-pulse mb-1"></div>
                    <div className="h-3 bg-gray-200 rounded w-16 animate-pulse"></div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop Table Loading */}
        <div className="hidden md:block">
          {Array.from({ length: 5 }).map((_, idx) => (
            <div key={idx} className="rounded-xl border shadow-sm bg-white border-gray-200">
              <div className="grid grid-cols-12 gap-4 px-4 py-4">
                <div className="col-span-2 flex items-center">
                  <div className="h-10 w-10 rounded-full bg-gray-200 animate-pulse mr-3"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded animate-pulse w-24 mb-1"></div>
                    <div className="h-3 bg-gray-200 rounded animate-pulse w-32"></div>
                  </div>
                </div>
                <div className="col-span-2">
                  <div className="h-6 bg-gray-200 rounded animate-pulse w-20"></div>
                </div>
                <div className="col-span-1">
                  <div className="h-4 bg-gray-200 rounded animate-pulse w-16"></div>
                </div>
                <div className="col-span-1">
                  <div className="h-4 bg-gray-200 rounded animate-pulse w-12"></div>
                </div>
                <div className="col-span-2">
                  <div className="h-6 bg-gray-200 rounded animate-pulse w-16"></div>
                </div>
                <div className="col-span-2">
                  <div className="h-4 bg-gray-200 rounded animate-pulse w-24"></div>
                </div>
                <div className="col-span-1">
                  <div className="h-4 bg-gray-200 rounded animate-pulse w-12"></div>
                </div>
                <div className="col-span-1">
                  <div className="h-6 bg-gray-200 rounded animate-pulse w-16"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </>
    ) : paymentsError ? (
      // Error state
      <div className="rounded-xl border shadow-sm bg-white border-gray-200 p-8 text-center">
        <div className="text-red-500 mb-2">
          <svg className="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <p className="text-gray-600 text-sm">Failed to load payment data</p>
        <p className="text-gray-400 text-xs mt-1">{paymentsError}</p>
      </div>
    ) : payments.length === 0 ? (
      // Empty state
      <div className="rounded-xl border shadow-sm bg-white border-gray-200 p-8 text-center">
        <div className="text-gray-400 mb-2">
          <svg className="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
          </svg>
        </div>
        <p className="text-gray-600 text-sm">No payments found</p>
      </div>
    ) : (
      <>
        {/* Mobile Card Layout */}
        <div className="md:hidden space-y-3">
          {payments.map((payment, index) => (
            <div
              key={index}
              className="bg-white border rounded-xl p-4 shadow-sm hover:bg-blue-50 transition border-gray-200 hover:border-blue-300"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <img
                    className="h-10 w-10 rounded-full object-cover border-2 border-gray-200 flex-shrink-0"
                    src={getPatientImage(payment, index)}
                    alt={payment.patient?.name || payment.patientName || payment.name || 'Patient'}
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {payment.patient?.name || payment.patientName || payment.name || 'Unknown Patient'}
                    </div>
                    <div className="text-xs text-gray-500">
                      {payment.patient?.email || payment.patientEmail || payment.email || 'No email'}
                    </div>
                  </div>
                </div>
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  (payment.status || '').toLowerCase() === 'completed' ? 'bg-green-100 text-green-800' :
                  (payment.status || '').toLowerCase() === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                  (payment.status || '').toLowerCase() === 'failed' ? 'bg-red-100 text-red-800' :
                  // (payment.status || '').toLowerCase() === 'refunded' ? 'bg-purple-100 text-purple-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  <div className={`w-1.5 h-1.5 rounded-full mr-1 ${
                    (payment.status || '').toLowerCase() === 'completed' ? 'bg-green-500' :
                    (payment.status || '').toLowerCase() === 'pending' ? 'bg-yellow-500' :
                    (payment.status || '').toLowerCase() === 'failed' ? 'bg-red-500' :
                    // (payment.status || '').toLowerCase() === 'refunded' ? 'bg-purple-500' :
                    'bg-gray-500'
                  }`} />
                  {(payment.status || 'Unknown').charAt(0).toUpperCase() + (payment.status || 'Unknown').slice(1)}
                </span>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPaymentTypeColor(payment.paymentType || payment.type)}`}>
                    {payment.paymentType || payment.type || 'Unknown'}
                  </span>
                  <div className="text-sm font-semibold text-gray-900">
                    {payment.amount ? 
                      (typeof payment.amount === 'string' ? 
                       `${payment.currency?.toUpperCase() || 'AUD'} $${payment.amount}` : 
                       new Intl.NumberFormat('en-US', {
                         style: 'currency',
                         currency: payment.currency?.toUpperCase() || 'AUD',
                         minimumFractionDigits: 0,
                         maximumFractionDigits: 2
                       }).format(payment.amount)) : 
                      'AUD $0'
                    }
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    {payment.method || payment.paymentMethod || 'Unknown'}
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-600">
                      {payment.date ? formatDateTime(payment.date) : 'No date'}
                    </div>
                    <div className="flex items-center space-x-2 mt-1">
                      <button 
                        onClick={() => downloadPaymentPDF(payment)}
                        className="text-green-600 hover:text-green-800 text-xs font-medium flex items-center"
                        title="Download PDF Receipt"
                      >
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Download
                      </button>
                      <button 
                        onClick={() => {
                          setSelectedPayment(payment);
                          setIsPopupOpen(true);
                        }}
                        className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                      >
                        View
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop Table Layout */}
        <div className="hidden md:block">
          {payments.map((payment, index) => (
            <div
              key={index}
              className="rounded-xl border transition-all duration-200 cursor-pointer shadow-sm hover:shadow-md bg-white border-gray-200 hover:border-blue-300 hover:bg-blue-50 mb-3"
            >
              <div className="grid grid-cols-12 gap-4 px-4 py-4">
                {/* Patient Info */}
                <div className="col-span-2 flex items-center">
                  <img
                    className="h-10 w-10 rounded-full object-cover mr-3 border-2 border-gray-200 flex-shrink-0"
                    src={getPatientImage(payment, index)}
                    alt={payment.patient?.name || payment.patientName || payment.name || 'Patient'}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm text-gray-900">
                      {payment.patient?.name || payment.patientName || payment.name || 'Unknown Patient'}
                    </div>
                    <div className="text-xs text-gray-500 break-words">
                      {payment.patient?.email || payment.patientEmail || payment.email || 'No email'}
                    </div>
                  </div>
                </div>

                {/* Payment Type */}
                <div className="col-span-2">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getPaymentTypeColor(payment.paymentType || payment.type)}`}>
                    {payment.paymentType || payment.type || 'Unknown'}
                  </span>
                </div>

                {/* Amount */}
                <div className="col-span-1 text-sm font-semibold text-gray-900">
                  {payment.amount ? 
                    (typeof payment.amount === 'string' ? 
                     `${payment.currency?.toUpperCase() || 'AUD'} $${payment.amount}` : 
                     new Intl.NumberFormat('en-US', {
                       style: 'currency',
                       currency: payment.currency?.toUpperCase() || 'AUD',
                       minimumFractionDigits: 0,
                       maximumFractionDigits: 2
                     }).format(payment.amount)) : 
                    'AUD $0'
                  }
                </div>

                {/* Method */}
                <div className="col-span-1 text-sm text-gray-600">
                  {payment.method || payment.paymentMethod || 'Unknown'}
                </div>

                {/* Status */}
                <div className="col-span-2">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                    (payment.status || '').toLowerCase() === 'completed' ? 'bg-green-100 text-green-800' :
                    (payment.status || '').toLowerCase() === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    (payment.status || '').toLowerCase() === 'failed' ? 'bg-red-100 text-red-800' :
                    // (payment.status || '').toLowerCase() === 'refunded' ? 'bg-purple-100 text-purple-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    <div className={`w-1.5 h-1.5 rounded-full mr-2 ${
                      (payment.status || '').toLowerCase() === 'completed' ? 'bg-green-500' :
                      (payment.status || '').toLowerCase() === 'pending' ? 'bg-yellow-500' :
                      (payment.status || '').toLowerCase() === 'failed' ? 'bg-red-500' :
                      // (payment.status || '').toLowerCase() === 'refunded' ? 'bg-purple-500' :
                      'bg-gray-500'
                    }`} />
                    {(payment.status || 'Unknown').charAt(0).toUpperCase() + (payment.status || 'Unknown').slice(1)}
                  </span>
                </div>

                {/* Date */}
                <div className="col-span-2 text-sm text-gray-600">
                  {payment.date ? formatDateTime(payment.date) : 'No date'}
                </div>

                {/* Invoice */}
                <div className="col-span-1">
                  <button 
                    onClick={() => {
                      setSelectedPayment(payment);
                      setIsPopupOpen(true);
                    }}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    View
                  </button>
                </div>

                {/* Actions */}
                <div className=" flex items-center justify-start">
                  <button 
                    onClick={() => downloadPaymentPDF(payment)}
                    className="p-1 rounded hover:bg-blue-100 text-blue-600 hover:text-blue-800 transition-colors"
                    title="Download PDF Receipt"
                  >
                    <svg
                      className="w-6 h-6 text-blue-600 hover:text-blue-800 transition-colors cursor-pointer"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4"
                      />
                    </svg>

                  </button>
                  {/* <button className="p-1 rounded hover:bg-gray-100 text-gray-400">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                      <path fillRule="evenodd" d="M1.323 11.447C2.811 6.976 7.028 3.75 12.001 3.75c4.97 0 9.185 3.223 10.675 7.69.12.362.12.752 0 1.113-1.487 4.471-5.705 7.697-10.677 7.697-4.97 0-9.186-3.223-10.675-7.69a1.762 1.762 0 010-1.113zM17.25 12a5.25 5.25 0 11-10.5 0 5.25 5.25 0 0110.5 0z" clipRule="evenodd"/>
                    </svg>
                  </button> */}
                  {/* <button className="p-1 rounded hover:bg-gray-100 text-gray-400">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"/>
                    </svg>
                  </button>
                  <button className="p-1 rounded hover:bg-gray-100 text-gray-400">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 6.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 12.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 18.75a.75.75 0 110-1.5.75.75 0 010 1.5z"/>
                    </svg>
                  </button> */}
                </div>
              </div>
            </div>
          ))}
        </div>
      </>
    )}
  </div>
</div>

          </div>

          {/* Pagination */}
          <div className="bg-white border-t border-gray-200 px-4 py-3 flex items-center justify-between">
            <div className="flex-1 flex justify-between sm:hidden">
              <button 
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage <= 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button 
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage >= pagination.totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{((pagination.currentPage - 1) * pagination.limit) + 1}</span> to{' '}
                  <span className="font-medium">{Math.min(pagination.currentPage * pagination.limit, pagination.total)}</span> of{' '}
                  <span className="font-medium">{pagination.total}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button 
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={pagination.currentPage <= 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Previous</span>
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                  
                  {/* Page numbers */}
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    let pageNum;
                    if (pagination.totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.currentPage >= pagination.totalPages - 2) {
                      pageNum = pagination.totalPages - 4 + i;
                    } else {
                      pageNum = pagination.currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          pageNum === pagination.currentPage
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  
                  <button 
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={pagination.currentPage >= pagination.totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Next</span>
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                </nav>
                        </div>
        </div>
      </div>

      {/* Payment Details Popup */}
      <PaymentVoicePopup
        isOpen={isPopupOpen}
        onClose={() => {
          setIsPopupOpen(false);
          setSelectedPayment(null);
        }}
        payment={selectedPayment}
      />
    </div>
  );
};

export default AdminPayments; 